// Pure state machine for the bottom-right messaging dock (issue #194).
//
// The whole dock is one discriminated union so illegal states are
// unrepresentable (engineering-principles invariant #4): there is never a
// "list and thread open at once" or an "open with no view" state to defend
// against. The view layer is a projection of `view`; `lastOpenView` is the
// restore target when reopening from the collapsed bubble (so minimizing a
// thread and clicking the bubble returns to that thread, LinkedIn-style).

export type DockView =
  | { readonly mode: 'collapsed' }
  | { readonly mode: 'list' }
  | { readonly mode: 'thread'; readonly conversationId: string };

/** The non-collapsed views the bubble can restore to. */
export type DockOpenView =
  | { readonly mode: 'list' }
  | { readonly mode: 'thread'; readonly conversationId: string };

export interface DockState {
  readonly view: DockView;
  readonly lastOpenView: DockOpenView;
}

export type DockAction =
  | { readonly type: 'toggle' }
  | { readonly type: 'openThread'; readonly conversationId: string }
  | { readonly type: 'back' }
  | { readonly type: 'close' };

export const initialDockState: DockState = {
  view: { mode: 'collapsed' },
  lastOpenView: { mode: 'list' },
};

export function dockReducer(state: DockState, action: DockAction): DockState {
  switch (action.type) {
    case 'toggle':
      return state.view.mode === 'collapsed'
        ? { ...state, view: state.lastOpenView }
        : { view: { mode: 'collapsed' }, lastOpenView: state.view };

    case 'openThread': {
      const view = { mode: 'thread', conversationId: action.conversationId } as const;
      return { view, lastOpenView: view };
    }

    case 'back':
      return state.view.mode === 'thread'
        ? { view: { mode: 'list' }, lastOpenView: { mode: 'list' } }
        : state;

    case 'close':
      return { view: { mode: 'collapsed' }, lastOpenView: { mode: 'list' } };
  }
}
